import * as moment from 'moment';
import { dateFormats } from '../../common';
import { ActualDataType, Dictionary, DigitalSet, DigitalState, EditSpotValueWithOptions, IDateRangePickerOptions, IValueViewModel, SpotValue, TagDefinition } from '../../models';
import { ICurrentApplicationService } from '../data';
import { IDateRangeOptionsBuilder, IFormatNumberHelper } from '../helpers';

export interface DigitalPoperties {
    digitalSetDictionary: { [code: number]: string };
    digitalStateCollection: DigitalState[];
    isDigital: boolean;
}

export interface ISpotValueHelperService {
    getDateOptions(): IDateRangePickerOptions;
    mapSpotValuesToEditSpotValuesWithOptions(
        spotValues: SpotValue[],
        digitalSets: Dictionary<DigitalSet>,
        tagDefinitions: Dictionary<TagDefinition>,
        viewModels: Dictionary<IValueViewModel>,
        callback: (sv: SpotValue, $index: number) => void
    ): EditSpotValueWithOptions[];
    getDigitalProperties(tagDefinition: TagDefinition, digitalSets: Dictionary<DigitalSet>): DigitalPoperties;
    getTitle(spotValue: SpotValue, isDigital: boolean, dic: { [code: number]: string }, decimals?: number): string;
}

export class SpotValueHelperService implements SpotValueHelperService {
    static serviceId = 'spotValueHelperService';

    static $inject = [
        '$translate',
        'currentApplicationService',
        'dateRangeOptionsBuilder',
        'formatNumberHelper'
    ];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _currentApplicationService: ICurrentApplicationService,
        private _dateRangeOptionsBuilder: IDateRangeOptionsBuilder,
        private _formatNumberHelper: IFormatNumberHelper
    ) {
    }

    // build options list for date picker
    public getDateOptions(): IDateRangePickerOptions {
        const appConfig = this._currentApplicationService.getApplicationConfiguration();
        const options: IDateRangePickerOptions = {
            applyClass: 'btn-primary',
            autoApply: true,
            autoUpdateInput: true,
            showDropdowns: true,
            singleDatePicker: true,
            timePicker24Hour: true,
            timePicker: true,
            timePickerIncrement: 1,
            locale: {
                format: dateFormats['m'],
                firstDay: appConfig.weekBeginDay
            }
        };
        return this._dateRangeOptionsBuilder.translate(options);
    }

    mapSpotValuesToEditSpotValuesWithOptions(
        spotValues: SpotValue[],
        digitalSets: Dictionary<DigitalSet>,
        tagDefinitions: Dictionary<TagDefinition>,
        viewModels: Dictionary<IValueViewModel>,
        callback: (sv: SpotValue, $index: number) => void
    ): EditSpotValueWithOptions[] {
        return spotValues.map((d, i) => {
            const tagDefinition = tagDefinitions[d.tagGuid];
            const { isDigital, digitalStateCollection, digitalSetDictionary } = this.getDigitalProperties(tagDefinition, digitalSets);
            const viewModel = viewModels[d.tagGuid];
            const ev: EditSpotValueWithOptions = Object.assign({}, d, {
                digitalStateCollection,
                digitalSetDictionary,
                isDigital,
                momentTimestamp: moment(d.timestamp),
                options: this.getOptions(d, i, callback),
                originalTimestamp: d.timestamp,
                tagDefinition,
                title: this.getTitle(d, isDigital, digitalSetDictionary),
                viewModel,
            });
            return ev;
        });
    }

    getDigitalProperties(tagDefinition: TagDefinition, digitalSets: Dictionary<DigitalSet>): DigitalPoperties {
        const isDigital = tagDefinition?.actualDataType === ActualDataType.Digital;
        let digitalStateCollection: DigitalState[] = [];
        const dic = {};
        if (isDigital) {
            digitalStateCollection = [{ code: null, name: '-' }]
                .concat(digitalSets[tagDefinition?.digitalSetGuid]?.stateCollection
                    .map(d => d) ?? []);
            digitalStateCollection.forEach(s => dic[s.code] = s.name);
        }
        return { isDigital, digitalStateCollection, digitalSetDictionary: dic };
    }

    getTitle(spotValue: SpotValue, isDigital: boolean, dic: { [code: number]: string }, decimals: number = 2): string {
        return spotValue.logbookEntryId
            ? this._$translate.instant('Readonly because value is linked to a logbook entry')
            : isDigital
                ? dic[spotValue.value] || '-'
                : this._formatNumberHelper.format(spotValue.value, decimals);
    }

    private getOptions(
        spotValue: SpotValue,
        $index: number,
        callback: (sv: SpotValue, $index: number) => void
    ): IDateRangePickerOptions {
        const dateOptions = this.getDateOptions();
        const eventHandlers = dateOptions.eventHandlers || {};
        const origCallback = eventHandlers['apply.daterangepicker'];
        eventHandlers['apply.daterangepicker'] = (evt, picker) => {
            if (origCallback) {
                origCallback(evt, picker);
            }
            callback(spotValue, $index);
        };
        dateOptions.eventHandlers = eventHandlers;
        return dateOptions;
    }
}